<template>
  <div :style="{ height: heightChart, width: '100%', position: 'relative' }">
    <component
        :is="chartComponent"
        :chart-data="datacollection"
        :options="chartOptions"
        :height="null"
        :width="null"
        style="height: 100%; width: 100%;"
        @chart-ready="$emit('chart-ready', $event)"
    ></component>
  </div>
</template>

<script>
import { Doughnut, Pie, mixins } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// Компонент-обертка
const DoughnutChart = {
  extends: Doughnut,
  mixins: [mixins.reactiveProp],
  props: ['options'],
  mounted() {
    this.addPlugin(ChartDataLabels);
    // Плагин для отрисовки текста (total) в центре
    this.addPlugin({
      id: 'center-text',
      beforeDraw: (chart) => {
        if (chart.config.options.elements.center) {
          const ctx = chart.ctx;
          const centerConfig = chart.config.options.elements.center;
          const fontStyle = centerConfig.fontStyle || 'RobotoRegular';
          const txt = centerConfig.text;
          const lines = Array.isArray(txt) ? txt : [txt];
          const color = centerConfig.color || '#000';
          const sidePadding = centerConfig.sidePadding || 20;
          const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);
          
          let baseFontSize = 20;
          ctx.font = `bold ${baseFontSize}px ${fontStyle}`; // Можно сделать динамическим как в Apex
          
          const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
          //const stringWidth = ctx.measureText(txt).width;
          const elementWidth = (chart.innerRadius * 2) - sidePaddingCalculated;

          const widthRatio = elementWidth / maxLineWidth;//stringWidth;
          const newFontSize = Math.floor(baseFontSize * widthRatio);

          const fontSizeToUse = Math.min(newFontSize, centerConfig.maxFontSize || 20);

          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          //const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
          //const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
          ctx.font = `bold ${fontSizeToUse}px ${fontStyle}`;
          ctx.fillStyle = color;

          const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
          let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
          const isGauge = chart.config.options.circumference === Math.PI;

          if (isGauge) {
            centerY += chart.innerRadius * 0.5; // коэффициент подбирается
          }

          // расстояние между строками
          const lineHeight = fontSizeToUse * 1.2;

          // смещение вверх, чтобы центрировать блок
          const totalHeight = lines.length * lineHeight;
          let startY = centerY - totalHeight / 2 + lineHeight / 2;

          // рисуем строки
          lines.forEach((line, i) => {
            ctx.fillText(line, centerX, startY + i * lineHeight);
          });

          //ctx.fillText(txt, centerX, centerY);
        }
      }
    });
    this.renderChart(this.chartData, this.options);
    this.$nextTick(() => {
        this.$emit('chart-ready', this.$data._chart);
    });
  }
}

const PieChart = {
  extends: Pie,
  mixins: [mixins.reactiveProp],
  props: ['options'],
  mounted() {
    this.addPlugin(ChartDataLabels);
    this.renderChart(this.chartData, this.options);
    this.$nextTick(() => {
        this.$emit('chart-ready', this.$data._chart);
    });
  }
}

export default {
  name: "RingChartJS",
  components: { DoughnutChart, PieChart },
  props: {
    typeChart: { type: String, default: 'donut' },
    heightChart: { type: String, default: '400px' },
    series: { type: Object },
    labels: { type: Array },
    colors: { type: Array },
    total: { type: String, default: "" },
    showTotal: { type: Boolean, default: true },
    asGauge: { type: Boolean, default: false },
    totalLabel: { type: String, default: "" },
    showLabels: { type: Boolean, default: true },
    showLegend: { type: Boolean, default: true },
    showToolTips: { type: Boolean, default: true },
    cutoutPercentage: { type: Number, default: 65 },
    centerText: { type: Array },
    click: Function
  },
  computed: {
    chartComponent() {
      return this.typeChart === 'pie' ? 'PieChart' : 'DoughnutChart';
    },
    datacollection() {
      return {
        labels: this.labels,
        datasets: [{
          backgroundColor: this.colors,
          borderWidth: 0,
          data: this.series.series
        }]
      }
    },
    chartOptions() {
        const isDonut = this.typeChart !== 'pie';
        const isGauge = isDonut && this.asGauge;
        return {
            responsive: true,
            maintainAspectRatio: false,
            rotation: isGauge ? Math.PI : -0.5 * Math.PI,
            circumference: isGauge ? Math.PI : 2 * Math.PI,
            cutoutPercentage: isDonut || isGauge ? this.cutoutPercentage : 0,
            legend: {
                display: this.showLegend,
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    fontColor: '#494f59',
                    padding: 15,
                    fontFamily: 'RobotoRegular',
                    boxWidth: 10,
                    fontSize: 12
                }
            },
            // Реализация клика
            onClick: (event, elements) => {
                if (elements && elements.length > 0) {
                    const index = elements[0]?._index;

                    if (index !== undefined && this.click) {
                        this.click(event, null, { 
                            dataPointIndex: index, 
                            seriesIndex: 0 
                        });
                    }
                }
            },
            elements: {
                center: {
                    text: isDonut && (this.showTotal || this.centerText) 
                          ? ( this.centerText 
                              ? this.centerText 
                              : (this.series.values.reduce((acc, value) => acc + value, 0)) + this.totalLabel)
                          : '',
                    color: '#494f59',
                    fontStyle: 'RobotoRegular',
                    sidePadding: 20,
                    maxFontSize: 20
                }
            },
            plugins: {
                datalabels: {
                    display: (ctx) => {
                        const value = ctx.dataset.data[ctx.dataIndex];
                        return this.showLabels && value !== 0;
                    },
                    color: '#fff',
                    font: { size: 14, weight: 'bold', family: 'RobotoRegular' },
                    anchor: this.typeChart === 'pie' ? 'end' : 'center',
                    align: this.typeChart === 'pie' ? 'start' : 'center',
                    offset: this.typeChart === 'pie' ? 20 : 0,
                    formatter: (value, ctx) => {
                        return this.series.values ? this.series.values[ctx.dataIndex] : value;
                    }
                }
            },
            tooltips: {
                enabled: this.showToolTips,
                callbacks: {
                    label: (tooltipItem, data) => {
                    const label = data.labels[tooltipItem.index];
                    const val = this.series.values ? this.series.values[tooltipItem.index] : data.datasets[0].data[tooltipItem.index];
                    return ` ${label}: ${val}`;
                    }
                }
            }
        }
    }
  }
}
</script>